"use client";

import React from "react";
import { Flame, ThermometerSnowflake, Droplets, Volume2, Timer, PowerOff, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const problems = [{
  icon: Flame,
  title: "No Hot Water",
  description: "Pilot light failure, faulty heating elements, or gas valve malfunctions preventing any hot water supply."
}, {
  icon: ThermometerSnowflake,
  title: "Water Heater Not Heating",
  description: "Lukewarm temperatures caused by failing thermostats, heavy tank sediment buildup, or electrical trips."
}, {
  icon: Droplets,
  title: "Water Heater Leaking",
  description: "Water pooling around the base from loose fittings, pressure valve release, or internal tank corrosion."
}, {
  icon: Volume2,
  title: "Water Heater Making Noise",
  description: "Popping, rumbling, or whistling noises typically resulting from calcified sediment trapped at the tank bottom."
}, {
  icon: Timer,
  title: "Hot Water Runs Out Quickly",
  description: "Short hot water duration due to broken dip tubes, undersized capacity, or one disabled element."
}, {
  icon: PowerOff,
  title: "Water Heater Keeps Shutting Off",
  description: "Repeated shutoffs triggered by faulty thermocouples, poor ventilation, or thermal safety switches tripping."
}];
export default function HeaterProblems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="waterheaterproblems" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs uppercase font-semibold tracking-wide border-primary/20 bg-secondary/50 text-secondary-foreground">
            Emergency Water Heater Triage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Water Heater Problems
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Hot water emergencies disrupt your home instantly. Our licensed Jonesboro plumbers diagnose and repair standard tank and tankless hot water system breakdowns quickly.
          </p>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {problems.map((problem, index) => {
          const Icon = problem.icon;
          return <motion.div key={problem.title} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card text-card-foreground border border-border shadow-xs hover:shadow-md transition-all duration-200 hover:-translate-y-1 rounded-xl p-0 flex flex-col justify-between">
                  <CardHeader className="pt-6 px-6 pb-2">
                    <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4 shrink-0">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground">
                      {problem.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-6 pb-6 pt-0">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {problem.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-12 sm:mt-16 text-center">
          <Button asChild size="lg" className="rounded-lg px-8 py-6 text-base font-semibold shadow-md">
            <Link to="/water-heater-repair/" className="inline-flex items-center gap-2">
              <span>View Water Heater Problems</span>
              <ArrowRight className="size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}